import React from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  useTheme,
  Card,
  CardContent,
  CardActions,
  Button,
  Divider,
  Avatar,
  LinearProgress,
  Stack,
  Alert,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  Close,
  PlayArrow,
  Stop,
  Visibility,
  MergeType,
  Schedule,
  CheckCircle,
  Restaurant,
  Info,
  DeleteForever,
} from "@mui/icons-material";
import KitchenIcon from "@mui/icons-material/Kitchen";

const PreviousRecipeCard = ({
  prevRecipe: task,
  prevNutriPot: stationSelected,
  isViewingPrevRecipe,
  onViewPrevRecipe,
  onClosePrevRecipe: onRemove,
  onContinueCooking,
  onStopCooking,
  onMergeCooking,
}) => {
  const theme = useTheme();
  const { recipe, tasks } = task || {};

  // Safely determine which NutriPot was selected
  const displayStation = React.useMemo(() => {
    // First try the explicitly passed stationSelected prop
    if (stationSelected) {
      return stationSelected === "np1" ? "Pot 1" : "Pot 2";
    }

    // If that's not available, try to get it from the first task
    if (tasks && tasks.length > 0 && tasks[0].cookIn) {
      return tasks[0].cookIn.toUpperCase();
    }

    // Fallback to NP1 if everything else fails
    return "Pot 1";
  }, [stationSelected, tasks]);

  // Calculate completion stats
  const completedTasks = React.useMemo(() => {
    if (!tasks || !Array.isArray(tasks)) return 0;
    return tasks.filter((task) => task.isOk || task.completed).length;
  }, [tasks]);

  const totalTasks = tasks?.length || 0;
  const completionPercentage =
    totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
  const isCompleted = completedTasks === totalTasks && totalTasks > 0;

  // Determine recipe status
  const getRecipeStatus = () => {
    if (isCompleted)
      return { label: "Completed", color: "success", icon: <CheckCircle /> };
    if (completedTasks > 0)
      return { label: "In Progress", color: "warning", icon: <Schedule /> };
    return { label: "Not Started", color: "info", icon: <Restaurant /> };
  };

  const status = getRecipeStatus();

  // If we don't have a recipe, show nothing
  if (!recipe) return null;

  return (
    <Card
      elevation={3}
      sx={{
        position: "relative",
        borderRadius: 3,
        overflow: "hidden",
        background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}10 100%)`,
        border: `2px solid ${theme.palette.primary.light}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: theme.shadows[8],
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      {/* Header with status indicator */}
      <Box
        sx={{
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          p: 1.5,
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                width: 32,
                height: 32,
              }}
            >
              <Restaurant fontSize="small" />
            </Avatar>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
              >
                Previously Saved Recipe
              </Typography>
              {/* <Typography
                variant="caption"
                sx={{ opacity: 0.9, fontSize: "0.75rem" }}
              >
                You can continue, view, or manage this recipe
              </Typography> */}
            </Box>
          </Box>

          <Tooltip title="Remove this recipe">
            <IconButton
              size="small"
              onClick={onRemove}
              sx={{
                color: "rgba(131, 23, 23, 0.8)",
                "&:hover": {
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <DeleteForever fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <CardContent sx={{ p: 2.5, pb: 1 }}>
        {/* Recipe Name and Status */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ flexGrow: 1, mr: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "1.1rem",
                color: theme.palette.text.primary,
                mb: 0.5,
                lineHeight: 1.3,
              }}
            >
              {recipe.recipe_name || "Recipe"}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: "0.875rem",
                lineHeight: 1.4,
              }}
            >
              This recipe was saved to continue later
            </Typography>
          </Box>

          {/* <Badge
            badgeContent={status.label}
            color={status.color}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.7rem",
                height: "20px",
                minWidth: "20px",
                borderRadius: "10px",
              },
            }}
          >
            <Avatar
              sx={{
                bgcolor: `${theme.palette[status.color].main}20`,
                color: theme.palette[status.color].main,
                width: 36,
                height: 36,
              }}
            >
              {status.icon}
            </Avatar>
          </Badge> */}
        </Box>

        {/* Progress Section */}
        {/* <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              fontWeight="medium"
              color="text.secondary"
            >
              Preparation Progress
            </Typography>
            <Typography variant="body2" fontWeight="bold" color={status.color}>
              {completedTasks}/{totalTasks} tasks
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: theme.palette.grey[200],
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
                background: `linear-gradient(90deg, ${
                  theme.palette[status.color].light
                } 0%, ${theme.palette[status.color].main} 100%)`,
              },
            }}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mt: 0.5,
              display: "block",
              fontSize: "0.75rem",
            }}
          >
            {isCompleted
              ? "✅ All ingredients are ready to cook!"
              : `${
                  totalTasks - completedTasks
                } ingredients still need preparation`}
          </Typography>
        </Box> */}

        {/* Station Info */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1.5,
            bgcolor: theme.palette.grey[50],
            borderRadius: 2,
            border: `1px solid ${theme.palette.grey[200]}`,
          }}
        >
          <KitchenIcon
            sx={{
              color: theme.palette.primary.main,
              fontSize: "1.2rem",
            }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="body2"
              color={theme.palette.primary.main}
              fontWeight="medium"
            >
              Assigned to: <strong>{displayStation}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This recipe is configured for the selected cooking station
            </Typography>
          </Box>
          <Chip
            label={displayStation}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {/* Info Alert */}
        {/* <Alert
          severity="info"
          icon={<Info fontSize="small" />}
          sx={{
            mt: 2,
            fontSize: "0.875rem",
            "& .MuiAlert-message": {
              fontSize: "0.8rem",
            },
          }}
        >
          <Typography variant="body2" fontSize="0.8rem">
            You can continue working on this recipe, view its details, or merge
            it with a new recipe.
          </Typography>
        </Alert> */}
      </CardContent>

      {/* Action Buttons */}
      {/* <CardActions
        sx={{
          p: 2,
          pt: 0,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
          {onContinueCooking && (
            <Tooltip title="Continue working on this recipe">
              <Button
                variant="contained"
                size="small"
                startIcon={<PlayArrow />}
                onClick={onContinueCooking}
                sx={{
                  flex: 1,
                  fontSize: "0.8rem",
                  py: 0.75,
                  background: `linear-gradient(45deg, ${theme.palette.success.main} 30%, ${theme.palette.success.dark} 90%)`,
                  "&:hover": {
                    background: `linear-gradient(45deg, ${theme.palette.success.dark} 30%, ${theme.palette.success.main} 90%)`,
                  },
                }}
              >
                Continue
              </Button>
            </Tooltip>
          )}

          {onViewPrevRecipe && (
            <Tooltip title="View recipe details and tasks">
              <Button
                variant="outlined"
                size="small"
                startIcon={<Visibility />}
                onClick={onViewPrevRecipe}
                sx={{
                  flex: 1,
                  fontSize: "0.8rem",
                  py: 0.75,
                }}
              >
                View
              </Button>
            </Tooltip>
          )}
        </Stack>

        {/* Secondary Actions */}
      {/* <Stack direction="row" spacing={1} sx={{ width: "100%", mt: 1 }}>
        {onMergeCooking && (
          <Tooltip title="Merge with new recipe">
            <Button
              variant="text"
              size="small"
              startIcon={<MergeType />}
              onClick={onMergeCooking}
              sx={{
                flex: 1,
                fontSize: "0.75rem",
                py: 0.5,
                color: theme.palette.primary.main,
              }}
            >
              Merge Recipe
            </Button>
          </Tooltip>
        )}

        {onStopCooking && (
          <Tooltip title="Stop and save for later">
            <Button
              variant="text"
              size="small"
              startIcon={<Stop />}
              onClick={onStopCooking}
              sx={{
                flex: 1,
                fontSize: "0.75rem",
                py: 0.5,
                color: theme.palette.error.main,
              }}
            >
              Stop Cooking
            </Button>
          </Tooltip>
        )}
      </Stack> */}
      {/* </CardActions> */}
    </Card>
  );
};

export default PreviousRecipeCard;
